import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector as useReduxSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { Typography, Tooltip } from "@material-ui/core";
import styled, { keyframes } from "styled-components";
import { PieChart } from "@mui/x-charts/PieChart";
import apiFetch from "../util/fetch-utils";
import WorkspaceHeader from "../common/WorkspaceHeader";
import { generateUrlForWorkspace } from "../Workspace/common";

/* ── Animations ───────────────────────────────────────────── */
const fadeIn = keyframes`from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; }`;

/* ── Layout ───────────────────────────────────────────────── */
const Page = styled.div`
    width: 100%;
    animation: ${fadeIn} 200ms ease;
`;

const Section = styled.div`
    margin-top: 24px;
`;

const SectionTitle = styled(Typography)`
    font-size: 0.72rem !important;
    font-weight: 700 !important;
    color: #9ca3af !important;
    text-transform: uppercase;
    letter-spacing: 0.6px !important;
    margin-bottom: 12px !important;
`;

/* ── Score card ───────────────────────────────────────────── */
const ScoreWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 32px;
    background: #fff;
    border: 1px solid #f0f0f0;
    border-radius: 14px;
    box-shadow: 0 2px 12px rgba(0,0,0,.06);
    padding: 24px 32px;
`;

const ScoreInfo = styled.div`
    flex: 1;
`;

const ScoreTitle = styled(Typography)`
    font-size: 1.1rem !important;
    font-weight: 700 !important;
    color: #111827 !important;
    margin-bottom: 6px !important;
`;

const ScoreSubtitle = styled(Typography)`
    font-size: 0.82rem !important;
    color: #6b7280 !important;
    line-height: 1.5 !important;
`;

const ScoreNumber = styled.div`
    font-size: 3rem;
    font-weight: 800;
    color: ${({ $score }) =>
        $score >= 80 ? "#16a34a" : $score >= 50 ? "#d97706" : "#dc2626"};
    line-height: 1;
    letter-spacing: -2px;
`;

const ScoreLabel = styled.div`
    font-size: 0.72rem;
    font-weight: 600;
    color: ${({ $score }) =>
        $score >= 80 ? "#16a34a" : $score >= 50 ? "#d97706" : "#dc2626"};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-family: "Inter", system-ui, sans-serif;
`;

/* ── Issue grid ───────────────────────────────────────────── */
const IssueGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 700px)  { grid-template-columns: 1fr; }
`;

const IssueCard = styled(React.forwardRef(({ $severity, ...props }, ref) => <NavLink ref={ref} {...props} />))`
    display: flex;
    align-items: center;
    gap: 16px;
    background: #fff;
    border: 1px solid ${({ $severity }) =>
        $severity === "critical" ? "#fecaca" :
        $severity === "high"     ? "#fde68a" :
        $severity === "medium"   ? "#ddd6fe" : "#e5e7eb"};
    border-radius: 12px;
    padding: 16px 18px;
    text-decoration: none;
    box-shadow: 0 2px 8px rgba(0,0,0,.04);
    transition: transform 120ms ease, box-shadow 120ms ease;
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(0,0,0,.09);
    }
`;

const IssueIcon = styled.div`
    width: 42px;
    height: 42px;
    border-radius: 10px;
    background: ${({ $bg }) => $bg};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    flex-shrink: 0;
`;

const IssueBody = styled.div`
    flex: 1;
    min-width: 0;
`;

const IssueLabel = styled(Typography)`
    font-size: 0.82rem !important;
    font-weight: 600 !important;
    color: #374151 !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const IssueDesc = styled(Typography)`
    font-size: 0.72rem !important;
    color: #9ca3af !important;
    margin-top: 1px !important;
`;

const IssueCount = styled.div`
    font-size: 1.4rem;
    font-weight: 800;
    color: ${({ $zero }) => $zero ? "#9ca3af" : ({ $severity }) =>
        $severity === "critical" ? "#dc2626" :
        $severity === "high"     ? "#d97706" :
        $severity === "medium"   ? "#7c3aed" : "#6b7280"};
    color: ${({ $zero, $severity }) =>
        $zero ? "#9ca3af" :
        $severity === "critical" ? "#dc2626" :
        $severity === "high"     ? "#d97706" :
        $severity === "medium"   ? "#7c3aed" : "#374151"};
    flex-shrink: 0;
    letter-spacing: -0.5px;
`;

/* ── Donut ────────────────────────────────────────────────── */
const DonutWrap = styled.div`
    flex-shrink: 0;
`;

/* ── Data fetching ────────────────────────────────────────── */
async function fetchCount(workspaceNumber, params) {
    const qs = new URLSearchParams({ workspaceNumber, size: 1, page: 0, ...params });
    const res = await apiFetch(`/api/monitored-uris/search/searchMonitoredUri?${qs}`);
    const json = await res.json();
    return json.page?.totalElements || 0;
}

const ISSUE_DEFS = [
    {
        key: "http4xx",
        label: "Client errors (4xx)",
        desc: "Pages returning 404, 403, 401…",
        icon: "🔴",
        iconBg: "#fee2e2",
        severity: "critical",
        params: { httpStatus: "400,401,403,404,410" },
        filterPath: "?httpStatus=400,401,403,404,410",
        weight: 20,
    },
    {
        key: "http5xx",
        label: "Server errors (5xx)",
        desc: "Pages returning 500, 503…",
        icon: "🔴",
        iconBg: "#fce7f3",
        severity: "critical",
        params: { httpStatus: "500,501,503,504" },
        filterPath: "?httpStatus=500,501,503,504",
        weight: 20,
    },
    {
        key: "robotsBlocked",
        label: "Blocked by robots.txt",
        desc: "Crawlers cannot access these pages",
        icon: "🤖",
        iconBg: "#fef9c3",
        severity: "high",
        params: { notIndexableReasons: "BLOCKED_BY_ROBOTS_TXT" },
        filterPath: "?notIndexableReasons=BLOCKED_BY_ROBOTS_TXT",
        weight: 12,
    },
    {
        key: "metaNoindex",
        label: "Meta noindex",
        desc: "Excluded from search engines via meta tag",
        icon: "🚫",
        iconBg: "#fef3c7",
        severity: "high",
        params: { notIndexableReasons: "META_NOINDEX" },
        filterPath: "?notIndexableReasons=META_NOINDEX",
        weight: 10,
    },
    {
        key: "xRobotsNoindex",
        label: "X-Robots noindex",
        desc: "Excluded via HTTP header",
        icon: "🚫",
        iconBg: "#fef3c7",
        severity: "high",
        params: { notIndexableReasons: "X_ROBOTS_NOINDEX" },
        filterPath: "?notIndexableReasons=X_ROBOTS_NOINDEX",
        weight: 10,
    },
    {
        key: "canonicalized",
        label: "Canonicalized away",
        desc: "Pages deferring to a different canonical URL",
        icon: "🔗",
        iconBg: "#ede9fe",
        severity: "medium",
        params: { notIndexableReasons: "CANONICALIZED" },
        filterPath: "?notIndexableReasons=CANONICALIZED",
        weight: 6,
    },
    {
        key: "titleDiff",
        label: "Title needs update",
        desc: "Recommended title differs from current",
        icon: "📝",
        iconBg: "#eff6ff",
        severity: "medium",
        params: { diffs: "Title" },
        filterPath: "?diffs=Title",
        weight: 5,
    },
    {
        key: "h1Diff",
        label: "H1 needs update",
        desc: "Recommended H1 differs from current",
        icon: "📝",
        iconBg: "#eff6ff",
        severity: "medium",
        params: { diffs: "H1" },
        filterPath: "?diffs=H1",
        weight: 5,
    },
    {
        key: "metaDiff",
        label: "Meta description needs update",
        desc: "Recommended meta description differs",
        icon: "📝",
        iconBg: "#eff6ff",
        severity: "medium",
        params: { diffs: "Meta" },
        filterPath: "?diffs=Meta",
        weight: 4,
    },
    {
        key: "redirectDiff",
        label: "Redirect recommendation pending",
        desc: "Destination URL recommendation not implemented",
        icon: "↪️",
        iconBg: "#f5f3ff",
        severity: "medium",
        params: { diffs: "Redirect" },
        filterPath: "?diffs=Redirect",
        weight: 4,
    },
    {
        key: "external",
        label: "External pages",
        desc: "Pages linking or redirecting off-site",
        icon: "🌐",
        iconBg: "#f0f9ff",
        severity: "info",
        params: { crawlScope: "external" },
        filterPath: "?crawlScope=external",
        weight: 0,
    },
    {
        key: "redirects",
        label: "Redirecting pages",
        desc: "Pages with at least one redirect hop",
        icon: "↩️",
        iconBg: "#f5f3ff",
        severity: "info",
        params: { httpStatus: "300,301,302,303,304,307,308" },
        filterPath: "?httpStatus=300,301,302,303,304,307,308",
        weight: 0,
    },
];

function computeScore(counts, total) {
    if (!total) return 100;
    let penalty = 0;
    for (const def of ISSUE_DEFS) {
        if (!def.weight) continue;
        const count = counts[def.key] || 0;
        const ratio = Math.min(count / total, 1);
        penalty += ratio * def.weight;
    }
    return Math.max(0, Math.round(100 - penalty));
}

function scoreLabel(score) {
    if (score >= 80) return "Healthy";
    if (score >= 50) return "Needs attention";
    return "Critical issues";
}

export default function AuditPage() {
    const { workspaceNumber } = useParams();
    const ws = useReduxSelector((s) => s.workspaces?.current);
    const wsStats = useReduxSelector((s) => s.stats?.workspaceStats);
    const total = wsStats?.statusCodeStats?.totalCount || 0;
    const wsUrl = generateUrlForWorkspace(workspaceNumber);

    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        Promise.all(
            ISSUE_DEFS.map(def =>
                fetchCount(workspaceNumber, def.params).then(n => ({ key: def.key, n }))
            )
        ).then(results => {
            const map = {};
            results.forEach(({ key, n }) => { map[key] = n; });
            setCounts(map);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [workspaceNumber]);

    const score = loading ? null : computeScore(counts, total);

    const criticalCount = ["http4xx", "http5xx"]
        .reduce((s, k) => s + (counts[k] || 0), 0);
    const highCount = ["robotsBlocked", "metaNoindex", "xRobotsNoindex"]
        .reduce((s, k) => s + (counts[k] || 0), 0);
    const mediumCount = ["canonicalized", "titleDiff", "h1Diff", "metaDiff", "redirectDiff"]
        .reduce((s, k) => s + (counts[k] || 0), 0);
    const okCount = Math.max(0, total - criticalCount - highCount - mediumCount);

    const pieData = [
        { id: "ok",       value: okCount,       color: "#10b981", label: "Healthy" },
        { id: "medium",   value: mediumCount,   color: "#a78bfa", label: "Medium" },
        { id: "high",     value: highCount,      color: "#f59e0b", label: "High" },
        { id: "critical", value: criticalCount,  color: "#ef4444", label: "Critical" },
    ].filter(d => d.value > 0);

    const byGroup = {
        critical: ISSUE_DEFS.filter(d => d.severity === "critical"),
        high:     ISSUE_DEFS.filter(d => d.severity === "high"),
        medium:   ISSUE_DEFS.filter(d => d.severity === "medium"),
        info:     ISSUE_DEFS.filter(d => d.severity === "info"),
    };

    const groups = [
        { key: "critical", label: "Critical Issues",  defs: byGroup.critical },
        { key: "high",     label: "High Priority",    defs: byGroup.high },
        { key: "medium",   label: "Medium Priority",  defs: byGroup.medium },
        { key: "info",     label: "Informational",    defs: byGroup.info },
    ];

    return (
        <Page>
            {ws && <WorkspaceHeader name={ws.name} url={ws.websiteUrl} countryCode={ws.countryCode} />}

            <Section>
                <ScoreWrap>
                    <DonutWrap>
                        <PieChart
                            slotProps={{ legend: { hidden: true } }}
                            width={140}
                            height={140}
                            margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
                            series={[{
                                data: pieData.length ? pieData : [{ id: "ok", value: 1, color: "#10b981" }],
                                highlightScope: { fade: "global", highlight: "item" },
                                innerRadius: 34,
                                outerRadius: 62,
                                paddingAngle: 2,
                                cornerRadius: 3,
                                cx: 66,
                                cy: 66,
                            }]}
                        />
                    </DonutWrap>

                    <ScoreInfo>
                        <ScoreTitle>Site Health Score</ScoreTitle>
                        <ScoreSubtitle>
                            Based on {total.toLocaleString()} monitored pages across {ISSUE_DEFS.filter(d => d.weight).length} issue categories.
                            Fix critical and high priority issues first for the biggest gains.
                        </ScoreSubtitle>
                    </ScoreInfo>

                    <div style={{ textAlign: "center" }}>
                        <ScoreNumber $score={score ?? 0}>
                            {loading ? "—" : score}
                        </ScoreNumber>
                        <ScoreLabel $score={score ?? 0}>
                            {loading ? "loading…" : scoreLabel(score)}
                        </ScoreLabel>
                    </div>
                </ScoreWrap>
            </Section>

            {groups.map(({ key, label, defs }) => (
                <Section key={key}>
                    <SectionTitle>{label}</SectionTitle>
                    <IssueGrid>
                        {defs.map(def => {
                            const count = counts[def.key];
                            const zero = !count;
                            return (
                                <Tooltip
                                    key={def.key}
                                    title={`Click to see these pages in the table`}
                                    arrow
                                    placement="top"
                                >
                                    <IssueCard
                                        to={wsUrl + def.filterPath}
                                        $severity={zero ? "info" : def.severity}
                                    >
                                        <IssueIcon $bg={def.iconBg}>{def.icon}</IssueIcon>
                                        <IssueBody>
                                            <IssueLabel>{def.label}</IssueLabel>
                                            <IssueDesc>{def.desc}</IssueDesc>
                                        </IssueBody>
                                        <IssueCount $zero={zero} $severity={def.severity}>
                                            {loading ? "…" : (count ?? 0).toLocaleString()}
                                        </IssueCount>
                                    </IssueCard>
                                </Tooltip>
                            );
                        })}
                    </IssueGrid>
                </Section>
            ))}
        </Page>
    );
}
